import { ThemeProvider } from '@mui/material';
import { Button, Card, notification } from 'antd';
import * as Api from 'api';
import ThresholdCard from 'components/profile/ThresholdCard';
import ThresholdCardHeader from 'components/profile/ThresholdCardHeader';
import { useEffect, useState } from 'react';
import outerTheme from 'theme/mui';
import classes from './style.module.scss';

const ProfileDetectionThreshold = () => {
  const [thresholds, setThresholds] = useState<any[]>([]);

  // Helper function to check if a category is a child of a parent category
  const getParentIndex = (index: number, data: any[] = thresholds): number | null => {
    const item = data[index];
    if (!item) return null;

    // Check if item has explicit parent_id
    if (item.parent_id) {
      const parentIndex = data.findIndex((t) => t.id === item.parent_id);
      return parentIndex >= 0 ? parentIndex : null;
    }

    // Infer parent from naming convention (e.g., "Deer (Antlered)" is child of "Deer")
    const match = item.category.match(/^(.+?)\s*\([^)]+\)$/);
    if (match) {
      const parentName = match[1].trim();
      const parentIndex = data.findIndex((t) => t.category === parentName && !t.category.includes('('));
      return parentIndex >= 0 ? parentIndex : null;
    }

    return null;
  };

  const isChildItem = (index: number): boolean =>
    getParentIndex(index) !== null;

  const getChildIndices = (index: number): number[] =>
    thresholds
      .map((_, i) => i)
      .filter((i) => getParentIndex(i) === index);

  useEffect(() => {
    Api.DETECTION_THRESHOLD().then(((res: any) => {
      console.log(res.text);
      const data = JSON.parse(res.text);
      setThresholds(data);
    })).catch((error: any) => {
      console.log('error ===>', error);
    });
  }, []);

  const changeValue = (index, value) => {
    const newData = JSON.parse(JSON.stringify(thresholds));
    newData[index].value = value;
    setThresholds(newData);
  };

  const changeEnable = (index, value) => {
    const newData = JSON.parse(JSON.stringify(thresholds));
    newData[index].enable = value;

    // If parent is disabled, disable all children
    if (value === 0) {
      const childIndices = getChildIndices(index);
      childIndices.forEach((childIndex) => {
        newData[childIndex].enable = 0;
      });
    }

    setThresholds(newData);
  };

  const getGroupIndices = (index: number): number[] =>
    [index, ...getChildIndices(index)];

  const handleDown = (index) => {
    if (isChildItem(index)) return;

    const groupA = getGroupIndices(index);
    const nextStart = groupA[groupA.length - 1] + 1;
    if (nextStart >= thresholds.length) return;

    const groupB = getGroupIndices(nextStart);
    const newData = [...thresholds];
    const groupAItems = groupA.map((i) => thresholds[i]);
    const groupBItems = groupB.map((i) => thresholds[i]);

    newData.splice(index, groupA.length + groupB.length, ...groupBItems, ...groupAItems);
    setThresholds(newData);
  };

  const handleUp = (index) => {
    if (isChildItem(index)) return;

    const groupA = getGroupIndices(index);
    if (groupA[0] === 0) return;

    let prevStart = groupA[0] - 1;
    if (isChildItem(prevStart)) {
      const parent = getParentIndex(prevStart);
      if (parent !== null) prevStart = parent;
    }

    if (prevStart < 0) return;

    const groupB = getGroupIndices(prevStart);
    const newData = [...thresholds];
    const groupAItems = groupA.map((i) => thresholds[i]);
    const groupBItems = groupB.map((i) => thresholds[i]);

    newData.splice(prevStart, groupB.length + groupA.length, ...groupAItems, ...groupBItems);
    setThresholds(newData);
  };

  const onSave = () => {
    console.log('save');

    const newData = JSON.parse(JSON.stringify(thresholds));
    let priorityCount = 0;

    // Assign priorities, accounting for parent-child relationships
    newData.forEach((threshold, index) => {
      if (!isChildItem(index)) {
        threshold.priority = priorityCount;
        priorityCount += 1;

        const childIndices = getChildIndices(index);
        childIndices.forEach((childIndex) => {
          newData[childIndex].priority = priorityCount;
          priorityCount += 1;
        });
      }
    });

    console.log(newData);
    Api.DETECTION_THRESHOLD_UPDATE(newData).then(((res: any) => {
      notification.open({
        message: res.text,
      });
    }));
  };

  return (
    <Card title="Detection Threshold" className={classes.profileCard} extra={<Button type="primary" onClick={onSave}> Save </Button>}>
      <ThemeProvider theme={outerTheme}>
        <ThresholdCardHeader />
        {thresholds.map((val, i) => {
          const parentIndex = getParentIndex(i);
          const parentItem = parentIndex !== null ? thresholds[parentIndex] : null;
          const parentEnabled = parentItem ? parentItem.enable == 1 : true;
          const isChild = isChildItem(i);

          return (
            <ThresholdCard
              key={i}
              value={val}
              index={i}
              changeValue={changeValue}
              changeEnable={changeEnable}
              handleDown={handleDown}
              handleUp={handleUp}
              isChild={isChild}
              parentEnabled={parentEnabled}
            />
          );
        })}
      </ThemeProvider>
    </Card>
  );
};

export default ProfileDetectionThreshold;
